/**
 * F-604-FE-01: useNotifications
 *
 * TanStack Query hook for doctor in-app notifications.
 * Polls GET /api/notifications every 60 seconds (refetchInterval).
 *
 * Returns:
 *   notifications  — array of NotificationItem (unread by default)
 *   unreadCount    — count of unread notifications
 *   markAsRead     — mutation: PATCH /api/notifications/:id/read, invalidates cache
 *   isLoading      — true while initial fetch is in progress
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NotificationType = 'NOTE_READY' | 'NOTE_FAILED' | 'PDF_READY' | 'DRAFT_REMINDER';

export interface NotificationMetadata {
  noteId?: string;
  sessionId?: string;
  downloadUrl?: string;
  errorSummary?: string;
  createdAt?: string;
  [key: string]: unknown;
}

export interface NotificationItem {
  id: string;
  doctorId: string;
  hospitalId: string;
  type: NotificationType;
  message: string;
  metadata: NotificationMetadata | null;
  read: boolean;
  createdAt: string;
}

interface NotificationsResponse {
  data: NotificationItem[];
  count: number;
  unreadCount: number;
}

// ---------------------------------------------------------------------------
// Query key
// ---------------------------------------------------------------------------

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useNotifications() {
  const queryClient = useQueryClient();

  // -------------------------------------------------------------------------
  // Query: GET /notifications (unread only)
  // Polls every 60 seconds.
  // -------------------------------------------------------------------------
  const {
    data,
    isLoading,
    isError,
  } = useQuery<NotificationsResponse>({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: () => api.get<NotificationsResponse>('/notifications'),
    refetchInterval: 60_000, // 60 seconds
    staleTime: 30_000,       // Consider fresh for 30s
  });

  // -------------------------------------------------------------------------
  // Mutation: PATCH /notifications/:id/read
  // Invalidates the notifications query on success to trigger a refetch.
  // -------------------------------------------------------------------------
  const markAsReadMutation = useMutation<
    { data: NotificationItem },
    Error,
    string
  >({
    mutationFn: (id: string) =>
      api.patch<{ data: NotificationItem }>(`/notifications/${id}/read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });

  return {
    notifications: data?.data ?? [],
    unreadCount: data?.unreadCount ?? 0,
    markAsRead: (id: string) => markAsReadMutation.mutate(id),
    isLoading,
    isError,
  };
}
